/**
 * domainsService — venda de domínio próprio para as Pages.
 *
 * O domínio é da CONTA: uma página responde na raiz (sorrisoreal.com) e as demais em
 * sorrisoreal.com/{slug}. Por isso a compra pede qual página vai para a raiz.
 *
 * As sugestões são apuradas FORA da tela — um processo diário lê as páginas publicadas, pede
 * nomes ao modelo, confere disponibilidade e preço no registrador e grava os candidatos.
 *
 * ⚠️ **Nada de estado de compra vive no navegador.** O QR do PIX, o token de confirmação e o
 * andamento do registro moram no servidor e voltam inteiros em `getStatus()`. Foi o que faltou na
 * primeira compra real (05/09/2026): o cliente pagou, recarregou a página e perdeu a cobrança.
 */
import api from './api';

const domainsService = {
  /**
   * O retrato da conta. **204 = a venda não está liberada para este usuário** (lançamento
   * restrito) — o container trata como "não renderizar o card", não como erro.
   *
   * É também o endpoint de POLL: o servidor cutuca a confirmação do pagamento antes de responder,
   * então uma chamada em laço basta enquanto o QR está na tela.
   *
   * @returns {Promise<null|{
   *   state:'empty'|'offer'|'awaiting_payment'|'owned',
   *   priceCents:number|null,
   *   canOffer:boolean,
   *   suggestions:{domain:string,rank:number}[],
   *   pages:{pageId:string,title:string,slug:string,type:number,suggestedRoot:boolean}[],
   *   openPurchase:null|{id:string,domain:string,priceCents:number,rootPageId:string,createdAt:string,
   *                      pix:null|{qrBase64:string|null,payload:string|null,expiresAt:string|null}},
   *   domains:{id:string,domain:string,status:string,dnsStatus:string|null,
   *            rootPage:{id:string,title:string,slug:string},
   *            expiresAt:string|null,updatedAt:string}[]
   *   (status 'failed' NUNCA chega: a API o devolve como 'paid', e não serve last_error — 09/10/2026)
   * }>}
   */
  async getStatus() {
    const res = await api.get('/v1/domains/status');
    return res.status === 204 || !res.data ? null : res.data;
  },

  /**
   * Confere um domínio digitado pelo cliente.
   *
   * O servidor limita a ~1 checagem a cada 12s por usuário (o fornecedor aceita 1 a cada 10s na
   * conta inteira). Por isso o container usa debounce — sem ele, digitar rápido rende 429.
   * @returns {Promise<{domain:string, available:boolean, reason:null|'taken'|'premium'|'too_expensive'|'invalid_domain'}>}
   */
  async check(domain) {
    const res = await api.get('/v1/domains/check', { params: { domain } });
    return res.data;
  },

  /**
   * Inicia a compra. Devolve o MESMO shape de `getStatus()`, já com a compra em aberto — a tela
   * troca o retrato inteiro em vez de costurar campo a campo o que acabou de acontecer.
   * @param {{domain:string, rootPageId:string, document?:string}} input
   */
  async purchase(input) {
    const res = await api.post('/v1/domains/purchase', input);
    return res.data;
  },

  /**
   * Desiste de uma compra ainda não paga. Devolve o estado novo, já sem ela.
   *
   * Sem esta saída, quem gerou o PIX e mudou de ideia ficava trancado: o guard de "uma compra em
   * aberto por vez" recusava qualquer nova tentativa.
   */
  async cancelPurchase(pageDomainId) {
    const res = await api.delete(`/v1/domains/purchase/${pageDomainId}`);
    return res.data;
  },

  /**
   * Troca qual página responde na RAIZ de um domínio já comprado. Devolve o estado novo.
   *
   * A escolha original é feita na compra, antes de o cliente ver o resultado; trocar depois é
   * natural (criou a página da empresa só agora, mudou de ideia). Sem isto, a única saída seria
   * comprar outro domínio.
   */
  async setRootPage(pageDomainId, rootPageId) {
    const res = await api.put(`/v1/domains/${pageDomainId}/root-page`, { rootPageId });
    return res.data;
  },
};

export default domainsService;
